import type {BillTodoType, DocumentCategory, DocumentDirection, LegalEntityType, ToCheckReason, VATCategory} from "../generated/enums.ts";
export enum SidebarSectionEnum {
    UNGROUPED = "UNGROUPED",
    CASH_FLOW = "CASH FLOW",
    INVOICING = "FACTUREN",
    OTHER = "OVERIG"
}

export enum ParentCategoryEnum {
    BANKING_AND_INSURANCE = "Banking & Insurance",
    BUSINESS_SERVICES = "Business Services",
    CAR_AND_EQUIPMENT = "Car & Equipment",
    FINANCING = "Financing",
    INCOME = "Income",
    INTERNAL_TRANSFERS = "Internal Transfers",
    INVESTMENT = "Investment",
    OFFICE_COSTS = "Office costs",
    OTHER_COSTS = "Other Costs",
    OWNERS_CURRENT_ACCOUNT = "Owner's Current Account",
    PAYROLL_TAX_PAYABLE = "Payroll Tax Payable",
    PAYROLL_TAX_RECEIVABLE = "Payroll Tax Receivable",
    PERSONNEL_COSTS = "Personnel costs",
    RENT_AND_FACILITIES = "Rent & Facilities",
    PURCHASE_COSTS = "Purchase Costs",
    SALES_AND_MARKETING = "Sales & Marketing",
    TAXES = "Taxes",
    VAT_PAYABLE = "VAT Payable",
    VAT_RECEIVABLE = "VAT Receivable",
    UNCATEGORIZED = "Uncategorized",
    // TODO: remove – only kept for backwards compatibility with legacy transaction categories
    INT_NON_OPERATIONAL = "INT_Non-operational",
    SOFTWARE = "Software",
    PRIVATE_WITHDRAWALS = "Private withdrawals",
    PROFESSIONAL_SERVICES = "Professional Services",
    OTHER_EXPENSES = "Other expenses",
    COST_OF_GOOD_SOLD = "Cost of good sold",
    REVENUE = "Revenue",
    PAYMENT_REQUESTS = "Payment requests",
}

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
    invoice: "Factuur",
    pos_receipt: "Bonnetje",
    credit_card_statement: "Creditcard afschrift",
    other: "Overig",
    archive: "Archief",
} as const;

export type RecategorizeCategoryType = Exclude<
    DocumentCategory,
    'other'
>;

export enum DocumentActivityStatusEnum {
    DUPLICATE = "duplicate",
    PROCESSING = "processing",
    IMPORTED = "imported",
}

export enum DocumentTagEnum {
    MATCHED = "matched",
    PRIVATELY_PAID = "privately_paid",
    WRITE_OFF = "write_off",
    POSSIBLE_DUPLICATE = "possible_duplicate",
    FIXED_ASSET = "fixed_asset",
}

export enum DocumentMatchingExclusionReasonEnum {
    PRIVATELY_PAID = "privately_paid",
}

export enum DropdownVATOptions {
    STANDARD = "S",
    REDUCED = "SR", //This is not a standard UBL XML value. Only used in FE to show a dropdown option for 9% VAT.
    EXEMPT = "E",
    REVERSE_CHARGE = "AE",
    ZERO = "Z"
}

export const mapDropdownVATToVATCategory = (vat: DropdownVATOptions): VATCategory => {
    if (vat === DropdownVATOptions.REDUCED) {
        return 'S'
    }
    if (vat === DropdownVATOptions.STANDARD) {
        return 'S'
    }
    if (vat === DropdownVATOptions.EXEMPT) {
        return 'E'
    }
    if (vat === DropdownVATOptions.REVERSE_CHARGE) {
        return 'AE'
    }
    if (vat === DropdownVATOptions.ZERO) {
        return 'Z'
    }
    return 'S'
}
export const mapVATCategoryToDropdownVAT = (vat: VATCategory, percentage: number): DropdownVATOptions => {
    if (vat === 'S') {
        if (percentage === 21) {
            return DropdownVATOptions.STANDARD
        }
        return DropdownVATOptions.REDUCED
    }
    if (vat === 'E') {
        return DropdownVATOptions.EXEMPT
    }
    if (vat === 'AE') {
        return DropdownVATOptions.REVERSE_CHARGE
    }
    if (vat === 'Z') {
        return DropdownVATOptions.ZERO
    }
    return DropdownVATOptions.STANDARD
}

export const VAT_DROPDOWN_OPTIONS: Array<{ value: DropdownVATOptions; label: string }> = [
    { value: DropdownVATOptions.STANDARD, label: "21% BTW hoog" },
    { value: DropdownVATOptions.REDUCED, label: "9% BTW laag" },
    { value: DropdownVATOptions.EXEMPT, label: "BTW vrijgesteld" },
    { value: DropdownVATOptions.REVERSE_CHARGE, label: "BTW verlegd" },
    { value: DropdownVATOptions.ZERO, label: "Geen btw" },
]

export const getVatPercentageForDropdownVAT = (vat: DropdownVATOptions): number | null => {
    switch (vat) {
        case DropdownVATOptions.STANDARD:
            return 21
        case DropdownVATOptions.REDUCED:
            return 9
        case DropdownVATOptions.ZERO:
            return 0
        default:
            return null
    }
}

export const TO_CHECK_REASON_LABELS: Record<ToCheckReason, string> = {
    awaiting_scan: "In verwerking",
    needs_category: "Categorie kiezen",
    needs_review: "Scan probleem",
} as const;

export const TO_CHECK_REASON_ORDER: ToCheckReason[] = [
    'needs_review',
    'needs_category',
    'awaiting_scan',
];

export const BILL_TODO_TYPE_LABELS: Record<BillTodoType, string> = {
    possible_duplicate: "Mogelijk duplicaat",
    transitory_schedule: "Transitorisch",
    before_bookkeeping_start: "Voor startdatum",
} as const;

export const BILL_TODO_TYPE_ORDER: BillTodoType[] = [
    'possible_duplicate',
    'transitory_schedule',
    'before_bookkeeping_start',
];

export const DOCUMENT_DIRECTION_LABELS: Record<DocumentDirection, string> = {
    purchase_invoice: "Inkoop",
    sales_invoice: "Verkoop",
    other: "Overig",
} as const;

export enum CashflowLabelEnum {
    CASH_START_OF_PERIOD = "CASH_START_OF_PERIOD",
    CASH_END_OF_PERIOD = "CASH_END_OF_PERIOD",
    OPERATIONS = "OPERATIONS",
    CASH_IN_SOURCES = "CASH_IN_SOURCES",
    CASH_OUT_SOURCES = "CASH_OUT_SOURCES",
    TOTAL = "TOTAL",
    NON_OPERATIONAL = "NON_OPERATIONAL",
    OPERATIONS_CASH = "OPERATIONS_CASH",
    NON_OPERATIONAL_CASH = "NON_OPERATIONAL_CASH",
    NET_CASHFLOW = "NET_CASHFLOW",
}

export const CASHFLOW_LABELS: Record<CashflowLabelEnum, string> = {
    [CashflowLabelEnum.CASH_START_OF_PERIOD]: "Cash begin van de periode",
    [CashflowLabelEnum.CASH_END_OF_PERIOD]: "Cash einde van de periode",
    [CashflowLabelEnum.OPERATIONS]: "Operationeel",
    [CashflowLabelEnum.CASH_IN_SOURCES]: "Cash Inflow bronnen",
    [CashflowLabelEnum.CASH_OUT_SOURCES]: "Cash Outflow bronnen",
    [CashflowLabelEnum.TOTAL]: "Totaal",
    [CashflowLabelEnum.NON_OPERATIONAL]: "Niet-operationeel",
    [CashflowLabelEnum.OPERATIONS_CASH]: "Operationele cash",
    [CashflowLabelEnum.NON_OPERATIONAL_CASH]: "Niet-operationele cash",
    [CashflowLabelEnum.NET_CASHFLOW]: "Netto cashflow",
} as const;

export enum BookingProvider {
    DEFAULT = 'DEFAULT',
    NONE = 'NONE'
}

export const isDefaultBookingProvider = (provider: BookingProvider) => provider === BookingProvider.DEFAULT;

export enum TransactionFlowFilterEnum {
    ANY = "any",
    INFLOW = "inflow",
    OUTFLOW = "outflow",
}

export enum TransactionMatchFilterEnum {
    ANY = "any",
    MATCHED = "matched",
    UNMATCHED = "unmatched",
    BOOKED_AWAY = "booked-away",
}

export enum TransactionAmountComparatorEnum {
    GT = "gt",
    LT = "lt",
}

export enum CategorizationSourceEnum {
    HUMAN = "HUMAN",
    CATEGORIZATION_ENGINE = "CATEGORIZATION_ENGINE",
    BUSINESS_RULE = "BUSINESS_RULE",
    AUTOMATION_RULE = "AUTOMATION_RULE",
    AUTOMATION_RULE_BACKFILL = "AUTOMATION_RULE_BACKFILL",
    INTERNAL_TRANSFER_DETECTION = "INTERNAL_TRANSFER_DETECTION",
    SAVINGS_MIRROR = "SAVINGS_MIRROR",
    CREDIT_CARD_SETTLEMENT = "CREDIT_CARD_SETTLEMENT",
    MERGE = "MERGE",
    MATCH_DERIVATION = "MATCH_DERIVATION",
    UNKNOWN = "UNKNOWN",
}

export const LEGAL_ENTITY_TYPE_LABELS: Record<LegalEntityType, string> = {
    'limited-liability': "B.V. (Besloten Vennootschap)",
    'general-partnership': "V.O.F. (Vennootschap onder Firma)",
    'sole-proprietorship': "Eenmanszaak / ZZP",
};

export const ALL_LEGAL_ENTITY_TYPES = Object.keys(LEGAL_ENTITY_TYPE_LABELS) as LegalEntityType[];

function isLegalEntityType(value: string): value is LegalEntityType {
    return Object.prototype.hasOwnProperty.call(LEGAL_ENTITY_TYPE_LABELS, value);
}

export function getLabelForLegalEntityType(value: string | null | undefined): string {
    if (!value) {
        return "-";
    }
    return isLegalEntityType(value) ? LEGAL_ENTITY_TYPE_LABELS[value] : value;
}

